/**
 * CONSAI - Invoice Form Component
 * Main form container for creating GST invoices
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { toast } from 'sonner';
import {
  Receipt, Plus, FileText, User, Calendar, Info, Percent, Package
} from 'lucide-react';
import ItemRow from './ItemRow';
import TotalsSummary from './TotalsSummary';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const InvoiceForm = ({
  open,
  onOpenChange,
  onSuccess,
  businessProfile,
}) => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  
  const [submitting, setSubmitting] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [states, setStates] = useState([]);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [suggestedInvoiceNumber, setSuggestedInvoiceNumber] = useState('');
  
  // Item search
  const [itemSearchQuery, setItemSearchQuery] = useState('');
  const [showItemDropdown, setShowItemDropdown] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    invoice_number: '',
    invoice_date: new Date().toISOString().split('T')[0],
    customer_id: '',
    customer_name: '',
    customer_address: '',
    customer_state: '',
    customer_gstin: '',
    customer_mobile: '',
    due_date: '',
    payment_terms: '',
    notes: '',
    discount_type: '',
    discount_value: 0,
    items: [{ item_id: '', description: '', hsn_sac: '', quantity: 1, unit: 'pcs', unit_price: 0, gst_percent: 18 }]
  });

  // Fetch states list
  const fetchStates = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/invoices/states/list`, { withCredentials: true });
      setStates(res.data.states || []);
    } catch (e) { /* ignore */ }
  };

  // Fetch customers
  const fetchCustomers = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/customers?per_page=100`, { withCredentials: true });
      setCustomers(res.data.customers || []);
    } catch (e) { /* ignore */ }
  };

  // Fetch inventory items
  const fetchInventoryItems = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/inventory?per_page=200`, { withCredentials: true });
      setInventoryItems(res.data.items || []);
    } catch (e) { /* ignore */ }
  };

  // Get suggested invoice number
  const fetchSuggestedNumber = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/invoices/next-number`, { withCredentials: true });
      setSuggestedInvoiceNumber(res.data.suggested_number);
      return res.data.suggested_number;
    } catch (e) {
      return 'INV-001';
    }
  };

  // Initialize form when dialog opens
  useEffect(() => {
    if (open && businessProfile) {
      const initializeForm = async () => {
        await Promise.all([fetchCustomers(), fetchStates(), fetchInventoryItems()]);
        const suggested = await fetchSuggestedNumber();
        
        setFormData({
          invoice_number: suggested,
          invoice_date: new Date().toISOString().split('T')[0],
          customer_id: '',
          customer_name: '',
          customer_address: '',
          customer_state: businessProfile.business_state || '',
          customer_gstin: '',
          customer_mobile: '',
          due_date: '',
          payment_terms: '',
          notes: '',
          discount_type: '',
          discount_value: 0,
          items: [{ item_id: '', description: '', hsn_sac: '', quantity: 1, unit: 'pcs', unit_price: 0, gst_percent: 18 }]
        });
      };
      initializeForm();
    }
  }, [open, businessProfile]);

  // Handle customer selection
  const handleCustomerSelect = (customerId) => {
    if (!customerId || customerId === 'new_customer') {
      setFormData(prev => ({
        ...prev,
        customer_id: '',
        customer_name: '',
        customer_address: '',
        customer_mobile: '',
      }));
      return;
    }

    const customer = customers.find(c => c.customer_id === customerId);
    if (customer) {
      setFormData(prev => ({
        ...prev,
        customer_id: customerId,
        customer_name: customer.name || '',
        customer_address: customer.address || '',
        customer_mobile: customer.mobile_number || '',
      }));
    }
  };

  // Handle inventory item selection
  const handleItemSelect = (idx, item) => {
    const newItems = [...formData.items];
    newItems[idx] = {
      item_id: item.item_id,
      description: item.item_name,
      hsn_sac: item.hsn_sac || '',
      quantity: 1,
      unit: item.unit || 'pcs',
      unit_price: item.selling_price || 0,
      gst_percent: item.gst_percent || 18
    };
    setFormData({ ...formData, items: newItems });
    setShowItemDropdown(null);
    setItemSearchQuery('');
  };

  // Update item
  const updateItem = (idx, field, value) => {
    const newItems = [...formData.items];
    newItems[idx] = { ...newItems[idx], [field]: value };
    // Clear item_id if description is manually changed
    if (field === 'description') {
      newItems[idx].item_id = '';
    }
    setFormData({ ...formData, items: newItems });
  };

  // Add item
  const addItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { item_id: '', description: '', hsn_sac: '', quantity: 1, unit: 'pcs', unit_price: 0, gst_percent: 18 }]
    });
  };

  // Remove item
  const removeItem = (idx) => {
    if (formData.items.length <= 1) return;
    setFormData({ ...formData, items: formData.items.filter((_, i) => i !== idx) });
  };

  // Calculate totals - Discount applied AFTER GST
  const calculateTotals = useCallback(() => {
    const businessState = businessProfile?.business_state || '';
    const customerState = formData.customer_state || '';
    const isInterstate = businessState && customerState && businessState.toLowerCase() !== customerState.toLowerCase();

    let subtotal = 0;
    let cgstTotal = 0;
    let sgstTotal = 0;
    let igstTotal = 0;

    formData.items.forEach(item => {
      const taxableValue = Number(item.quantity) * Number(item.unit_price);
      subtotal += taxableValue;

      if (isInterstate) {
        igstTotal += taxableValue * (Number(item.gst_percent) / 100);
      } else {
        const halfRate = Number(item.gst_percent) / 2;
        cgstTotal += taxableValue * (halfRate / 100);
        sgstTotal += taxableValue * (halfRate / 100);
      }
    });

    const totalTax = cgstTotal + sgstTotal + igstTotal;
    const subtotalWithGst = subtotal + totalTax;

    let discountAmount = 0;
    if (formData.discount_type && formData.discount_type !== 'none' && formData.discount_value > 0) {
      if (formData.discount_type === 'percent') {
        discountAmount = subtotalWithGst * (Number(formData.discount_value) / 100);
      } else {
        discountAmount = Number(formData.discount_value);
      }
    }

    const grandTotalRaw = subtotalWithGst - discountAmount;
    const grandTotal = Math.round(grandTotalRaw);
    const roundOff = grandTotal - grandTotalRaw;

    return {
      subtotal,
      cgstTotal,
      sgstTotal,
      igstTotal,
      totalTax,
      subtotalWithGst,
      discountAmount,
      roundOff,
      grandTotal,
      isInterstate
    };
  }, [formData, businessProfile]);

  const totals = calculateTotals();

  // Submit invoice
  const handleSubmit = async (e) => {
    e.preventDefault();

    const validItems = formData.items.filter(i => i.description.trim());
    if (validItems.length === 0) {
      toast.error(language === 'hi' ? 'कम से कम एक आइटम जोड़ें' : 'Add at least one item');
      return;
    }

    if (!formData.customer_name.trim()) {
      toast.error(language === 'hi' ? 'ग्राहक का नाम आवश्यक है' : 'Customer name is required');
      return;
    }

    if (formData.discount_type === 'percent' && formData.discount_value > 100) {
      toast.error(language === 'hi' ? 'छूट 100% से अधिक नहीं हो सकती' : 'Discount cannot exceed 100%');
      return;
    }
    if (formData.discount_type === 'fixed' && formData.discount_value > totals.subtotalWithGst) {
      toast.error(language === 'hi' ? 'छूट कुल राशि से अधिक नहीं हो सकती' : 'Discount cannot exceed total amount');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        invoice_number: formData.invoice_number || null,
        invoice_date: formData.invoice_date ? formData.invoice_date.split('-').reverse().join('-') : null,
        customer_id: formData.customer_id || null,
        customer_name: formData.customer_name,
        customer_address: formData.customer_address || null,
        customer_state: formData.customer_state || null,
        customer_gstin: formData.customer_gstin || null,
        customer_mobile: formData.customer_mobile || null,
        due_date: formData.due_date ? formData.due_date.split('-').reverse().join('-') : null,
        payment_terms: formData.payment_terms || null,
        notes: formData.notes || null,
        discount_type: formData.discount_type || null,
        discount_value: formData.discount_value || 0,
        items: validItems.map(item => ({
          item_id: item.item_id || null,
          description: item.description,
          hsn_sac: item.hsn_sac || null,
          quantity: Number(item.quantity),
          unit: item.unit,
          unit_price: Number(item.unit_price),
          gst_percent: Number(item.gst_percent)
        }))
      };

      await axios.post(`${API_URL}/api/invoices/gst`, payload, { withCredentials: true });
      toast.success(language === 'hi' ? 'इनवॉइस बनाया गया' : 'Invoice created successfully');
      // Trigger onboarding completion if active
      if (window.triggerOnboardingComplete) window.triggerOnboardingComplete('invoice');
      onOpenChange(false);
      if (onSuccess) onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || (language === 'hi' ? 'विफल' : 'Failed to create invoice'));
    }
    setSubmitting(false);
  };

  const isCustomerLocked = formData.customer_id && formData.customer_id !== 'new_customer';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[calc(100vw-2rem)] sm:max-w-3xl max-h-[85vh] sm:max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-manrope flex items-center gap-2 text-base sm:text-lg">
            <FileText className="h-4 w-4 sm:h-5 sm:w-5 text-[#021945]" />
            {language === 'hi' ? 'नया GST इनवॉइस' : 'New GST Invoice'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
          {/* Business GST Info */}
          {businessProfile && (
            <div className="bg-green-50 border border-green-200 p-2 sm:p-3 rounded-lg">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-xs sm:text-sm font-medium text-green-800 truncate">{businessProfile.business_name}</p>
                  <p className="text-xs text-green-600 truncate">GSTIN: {businessProfile.gst_number}</p>
                  {businessProfile.business_state && (
                    <p className="text-xs text-green-600">{businessProfile.business_state}</p>
                  )}
                </div>
                <Button
                  type="button"
                  variant="link"
                  size="sm"
                  className="text-green-700 text-xs p-0 h-auto self-start sm:self-center"
                  onClick={() => navigate('/profile')}
                >
                  {language === 'hi' ? 'प्रोफाइल में बदलें' : 'Edit in Profile'}
                </Button>
              </div>
            </div>
          )}

          {/* Invoice Details Section */}
          <div className="bg-slate-50 p-3 sm:p-4 rounded-lg space-y-3 sm:space-y-4">
            <h3 className="font-semibold text-slate-700 font-manrope flex items-center gap-2 text-sm sm:text-base">
              <Receipt className="h-4 w-4" />
              {language === 'hi' ? 'इनवॉइस विवरण' : 'Invoice Details'}
            </h3>
            <div className="grid grid-cols-2 gap-2 sm:gap-4">
              <div>
                <Label className="text-xs text-slate-500">{language === 'hi' ? 'इनवॉइस नंबर' : 'Invoice Number'}</Label>
                <Input
                  value={formData.invoice_number}
                  onChange={(e) => setFormData({ ...formData, invoice_number: e.target.value })}
                  placeholder={suggestedInvoiceNumber || "INV-001"}
                  className="text-sm"
                  data-testid="invoice-number-input"
                />
                <p className="text-[10px] sm:text-xs text-slate-400 mt-1">{language === 'hi' ? 'कोई भी फॉर्मेट' : 'Any format'}</p>
              </div>
              <div>
                <Label className="text-xs text-slate-500">{language === 'hi' ? 'तारीख' : 'Date'}</Label>
                <Input
                  type="date"
                  value={formData.invoice_date}
                  onChange={(e) => setFormData({ ...formData, invoice_date: e.target.value })}
                  className="text-sm"
                  data-testid="invoice-date-input"
                />
              </div>
            </div>
          </div>

          {/* Bill To Section */}
          <div className="bg-slate-50 p-3 sm:p-4 rounded-lg space-y-3 sm:space-y-4">
            <h3 className="font-semibold text-slate-700 font-manrope flex items-center gap-2 text-sm sm:text-base">
              <User className="h-4 w-4" />
              {language === 'hi' ? 'ग्राहक विवरण (Bill To)' : 'Customer Details (Bill To)'}
            </h3>

            {/* Customer Selection */}
            <div>
              <Label className="text-xs text-slate-500">{language === 'hi' ? 'मौजूदा ग्राहक चुनें' : 'Select Existing Customer'}</Label>
              <Select value={formData.customer_id} onValueChange={handleCustomerSelect}>
                <SelectTrigger className="text-sm" data-testid="customer-select">
                  <SelectValue placeholder={language === 'hi' ? 'ग्राहक चुनें या नया दर्ज करें' : 'Select customer or enter new'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="new_customer">{language === 'hi' ? 'नया ग्राहक' : 'New Customer'}</SelectItem>
                  {customers.map(c => (
                    <SelectItem key={c.customer_id} value={c.customer_id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {isCustomerLocked && (
                <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                  <Info className="h-3 w-3" />
                  {language === 'hi' ? 'ग्राहक विवरण बदलने के लिए ग्राहक सेक्शन में जाएं' : 'To edit customer details, go to Customers section'}
                </p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-2 sm:gap-4">
              <div>
                <Label className="text-xs text-slate-500">{language === 'hi' ? 'नाम' : 'Name'} *</Label>
                <Input
                  value={formData.customer_name}
                  onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                  placeholder={language === 'hi' ? 'ग्राहक का नाम' : 'Customer name'}
                  className={`text-sm ${isCustomerLocked ? 'bg-slate-100' : ''}`}
                  required
                  disabled={isCustomerLocked}
                  data-testid="customer-name-input"
                />
              </div>
              <div>
                <Label className="text-xs text-slate-500">{language === 'hi' ? 'मोबाइल' : 'Mobile'}</Label>
                <Input
                  value={formData.customer_mobile}
                  onChange={(e) => setFormData({ ...formData, customer_mobile: e.target.value })}
                  placeholder="9876543210"
                  className={`text-sm ${isCustomerLocked ? 'bg-slate-100' : ''}`}
                  disabled={isCustomerLocked}
                  data-testid="customer-mobile-input"
                />
              </div>
            </div>

            <div>
              <Label className="text-xs text-slate-500">{language === 'hi' ? 'पता' : 'Address'}</Label>
              <Textarea
                value={formData.customer_address}
                onChange={(e) => setFormData({ ...formData, customer_address: e.target.value })}
                placeholder={language === 'hi' ? 'ग्राहक का पूरा पता' : 'Customer full address'}
                className="text-sm h-14 sm:h-16"
                data-testid="customer-address-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-2 sm:gap-4">
              <div>
                <Label className="text-xs text-slate-500">
                  {language === 'hi' ? 'राज्य' : 'State'} *
                </Label>
                <Select value={formData.customer_state} onValueChange={(v) => setFormData({ ...formData, customer_state: v })}>
                  <SelectTrigger className="text-sm" data-testid="customer-state-select">
                    <SelectValue placeholder={language === 'hi' ? 'राज्य चुनें' : 'Select state'} />
                  </SelectTrigger>
                  <SelectContent>
                    {states.map(s => (
                      <SelectItem key={s.code} value={s.name}>{s.name} ({s.code})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {totals.isInterstate && (
                  <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                    <Info className="h-3 w-3" />
                    {language === 'hi' ? 'अंतर्राज्यीय - IGST लागू होगा' : 'Interstate - IGST will apply'}
                  </p>
                )}
                {!totals.isInterstate && formData.customer_state && (
                  <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                    <Info className="h-3 w-3" />
                    {language === 'hi' ? 'अंतर्राज्यीय - CGST+SGST लागू होगा' : 'Intrastate - CGST+SGST will apply'}
                  </p>
                )}
              </div>
              <div>
                <Label className="text-xs text-slate-500">GSTIN ({language === 'hi' ? 'B2B के लिए' : 'for B2B'})</Label>
                <Input
                  value={formData.customer_gstin}
                  onChange={(e) => setFormData({ ...formData, customer_gstin: e.target.value.toUpperCase() })}
                  placeholder="22AAAAA0000A1Z5"
                  className="text-sm uppercase"
                  maxLength={15}
                  data-testid="customer-gstin-input"
                />
              </div>
            </div>
          </div>

          {/* Items Section */}
          <div className="space-y-4">
            <h3 className="font-semibold text-slate-700 font-manrope flex items-center gap-2">
              <Package className="h-4 w-4" />
              {language === 'hi' ? 'आइटम' : 'Items'}
            </h3>

            {formData.items.map((item, idx) => (
              <ItemRow
                key={idx}
                item={item}
                index={idx}
                onUpdateItem={updateItem}
                onRemoveItem={removeItem}
                canRemove={formData.items.length > 1}
                inventoryItems={inventoryItems}
                onItemSelect={handleItemSelect}
                showItemDropdown={showItemDropdown}
                setShowItemDropdown={setShowItemDropdown}
                itemSearchQuery={itemSearchQuery}
                setItemSearchQuery={setItemSearchQuery}
              />
            ))}

            <Button type="button" variant="outline" size="sm" onClick={addItem} className="">
              <Plus className="h-3 w-3 mr-1" />{language === 'hi' ? 'आइटम जोड़ें' : 'Add Item'}
            </Button>

            <p className="text-xs text-slate-400 flex items-center gap-1">
              <Info className="h-3 w-3" />
              {language === 'hi'
                ? 'HSN/SAC वैकल्पिक है। नए आइटम स्वचालित रूप से इन्वेंटरी में सहेजे जाएंगे।'
                : 'HSN/SAC is optional. New items will be auto-saved to inventory.'}
            </p>
          </div>

          {/* Discount Section */}
          <div className="bg-amber-50 border border-amber-200 p-4 rounded-lg space-y-3">
            <h3 className="font-semibold text-amber-800 font-manrope flex items-center gap-2">
              <Percent className="h-4 w-4" />
              {language === 'hi' ? 'छूट (कुल राशि पर)' : 'Discount (On Total Amount)'}
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs text-amber-700">{language === 'hi' ? 'छूट प्रकार' : 'Discount Type'}</Label>
                <Select value={formData.discount_type} onValueChange={(v) => setFormData({ ...formData, discount_type: v })}>
                  <SelectTrigger className="text-sm">
                    <SelectValue placeholder={language === 'hi' ? 'कोई छूट नहीं' : 'No discount'} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">{language === 'hi' ? 'कोई छूट नहीं' : 'No discount'}</SelectItem>
                    <SelectItem value="percent">% ({language === 'hi' ? 'प्रतिशत' : 'Percentage'})</SelectItem>
                    <SelectItem value="fixed">Rs. ({language === 'hi' ? 'निश्चित राशि' : 'Fixed Amount'})</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {formData.discount_type && formData.discount_type !== 'none' && (
                <div>
                  <Label className="text-xs text-amber-700">
                    {formData.discount_type === 'percent'
                      ? (language === 'hi' ? 'छूट %' : 'Discount %')
                      : (language === 'hi' ? 'छूट राशि (Rs.)' : 'Discount Amount (Rs.)')}
                  </Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    max={formData.discount_type === 'percent' ? 100 : totals.subtotalWithGst}
                    value={formData.discount_value}
                    onChange={(e) => setFormData({ ...formData, discount_value: e.target.value })}
                    className="text-sm"
                    data-testid="discount-value-input"
                  />
                </div>
              )}
            </div>
            <p className="text-xs text-amber-600 flex items-center gap-1">
              <Info className="h-3 w-3" />
              {language === 'hi' ? 'छूट GST सहित कुल राशि पर लागू होगी' : 'Discount will be applied on total amount including GST'}
            </p>
          </div>

          {/* Totals Section */}
          <TotalsSummary
            totals={totals}
            discountType={formData.discount_type}
            discountValue={formData.discount_value}
          />

          {/* Payment Terms Section */}
          <div className="bg-slate-50 p-4 rounded-lg space-y-4">
            <h3 className="font-semibold text-slate-700 font-manrope flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              {language === 'hi' ? 'भुगतान विवरण' : 'Payment Details'}
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs text-slate-500">{language === 'hi' ? 'भुगतान देय तारीख' : 'Payment Due Date'}</Label>
                <Input
                  type="date"
                  value={formData.due_date}
                  onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                  className="text-sm"
                  data-testid="due-date-input"
                />
              </div>
              <div>
                <Label className="text-xs text-slate-500">{language === 'hi' ? 'भुगतान शर्तें' : 'Payment Terms'}</Label>
                <Input
                  value={formData.payment_terms}
                  onChange={(e) => setFormData({ ...formData, payment_terms: e.target.value })}
                  placeholder={language === 'hi' ? 'जैसे: 30 दिन में भुगतान' : 'e.g., Payment within 30 days'}
                  className="text-sm"
                  data-testid="payment-terms-input"
                />
              </div>
            </div>
          </div>

          {/* Notes Section */}
          <div>
            <Label className="text-xs text-slate-500">{language === 'hi' ? 'नोट्स / टिप्पणियाँ' : 'Notes / Comments'}</Label>
            <Textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder={language === 'hi' ? 'ग्राहक के लिए कोई अतिरिक्त जानकारी' : 'Any additional information for the customer'}
              className="text-sm h-16"
              data-testid="notes-input"
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="">
              {language === 'hi' ? 'रद्द करें' : 'Cancel'}
            </Button>
            <Button
              type="submit"
              className="bg-[#021945] hover:bg-[#021945]/90"
              disabled={submitting}
              data-testid="invoice-submit-btn"
            >
              {submitting ? (
                <span className="flex items-center gap-2">
                  <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                  {language === 'hi' ? 'बना रहा है...' : 'Creating...'}
                </span>
              ) : (
                language === 'hi' ? 'इनवॉइस बनाएं' : 'Create Invoice'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>

      {/* Click outside to close item dropdown */}
      {showItemDropdown !== null && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setShowItemDropdown(null)}
        />
      )}
    </Dialog>
  );
};

export default InvoiceForm;
